import React, { useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

export type UnifiedModalVariant = 'drawer' | 'center';
export type UnifiedModalSide = 'right' | 'left';
export type UnifiedModalSize = 'sm' | 'md' | 'lg' | number | string;

export type UnifiedModalProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  variant?: UnifiedModalVariant;
  side?: UnifiedModalSide; // for drawer
  size?: UnifiedModalSize; // width for center, width for drawer (optional)
  fullHeight?: boolean; // drawer defaults to true
  closeOnOverlay?: boolean; // default true
  closeOnEsc?: boolean; // default true
  showClose?: boolean; // default true
  ariaLabel?: string;
  ariaLabelledBy?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
};

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 5000;
  display: flex;
`;

const DrawerWrap = styled.aside<{
  $side: UnifiedModalSide;
  $width?: string;
  $fullHeight?: boolean;
}>`
  position: relative;
  height: ${({ $fullHeight }) => ($fullHeight ? '100dvh' : 'auto')};
  width: ${({ $width }) => $width || '60vw'};
  max-width: 1100px;
  min-width: 360px;
  background: var(--card-bg, #ffffff);
  border-left: ${({ $side }) => ($side === 'right' ? '1px solid var(--card-border, rgba(17,24,39,0.08))' : 'none')};
  border-right: ${({ $side }) => ($side === 'left' ? '1px solid var(--card-border, rgba(17,24,39,0.08))' : 'none')};
  box-shadow: ${({ $side }) => ($side === 'right' ? '-12px 0 30px rgba(0,0,0,0.15)' : '12px 0 30px rgba(0,0,0,0.15)')};
  z-index: 5100;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100vw;
    max-width: 100vw;
    min-width: 100vw;
    height: 100dvh;
  }
`;

const CenterCard = styled.div<{ $width?: string; $fullHeight?: boolean }>`
  width: ${({ $width }) => $width || 'min(680px, 92vw)'};
  background: var(--card-bg, #ffffff);
  color: var(--text, #111827);
  border-radius: 14px;
  border: 1px solid var(--card-border, rgba(0,0,0,0.08));
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  z-index: 5100;
  display: flex;
  flex-direction: column;
  height: ${({ $fullHeight }) => ($fullHeight ? '100dvh' : 'auto')};
  max-height: 100dvh;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  font-weight: 800;
  color: #444;
`;

const Body = styled.div<{ $scroll?: boolean }>`
  position: relative;
  padding: 1rem 1.25rem;
  ${({ $scroll }) => ($scroll ? css`
    flex: 1 1 auto;
    min-height: 0; /* allow content to shrink within flex container */
    overflow: auto;
  ` : '')}
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  padding: 0.9rem 1.25rem;
  border-top: 1px solid rgba(0,0,0,0.06);
`;

const CloseButton = styled.button`
  border: 0;
  background: transparent;
  font-weight: 900;
  font-size: 1.25rem;
  cursor: pointer;
`;

function toCssWidth(size?: UnifiedModalSize): string | undefined {
  if (size == null) return undefined;
  if (typeof size === 'number') return `${size}px`;
  if (size === 'sm') return 'min(520px, 92vw)';
  if (size === 'md') return 'min(680px, 92vw)';
  if (size === 'lg') return 'min(920px, 94vw)';
  if (typeof size === 'string') return size;
  return undefined;
}

const UnifiedModal: React.FC<UnifiedModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  variant = 'drawer',
  side = 'right',
  size,
  fullHeight,
  closeOnOverlay = true,
  closeOnEsc = true,
  showClose = true,
  ariaLabel,
  ariaLabelledBy,
  initialFocusRef,
}) => {
  const lastActiveEl = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const width = useMemo(() => toCssWidth(size), [size]);

  const isFullHeight = fullHeight ?? (variant === 'drawer');

  // Body scroll lock + focus management
  useEffect(() => {
    if (!open) return;
    lastActiveEl.current = document.activeElement as HTMLElement;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTarget = initialFocusRef?.current || containerRef.current?.querySelector<HTMLElement>('input, button, select, textarea, [tabindex]:not([tabindex="-1"])');
    setTimeout(() => focusTarget?.focus?.(), 0);

    return () => {
      document.body.style.overflow = prev;
      lastActiveEl.current?.focus?.();
    };
  }, [open, initialFocusRef]);

  // ESC handling
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEsc, onClose]);

  if (!open) return null;

  const labelledBy = ariaLabelledBy ? { 'aria-labelledby': ariaLabelledBy } : {};
  const label = ariaLabel ? { 'aria-label': ariaLabel } : {};

  return (
    <Backdrop
      role="presentation"
      onClick={(e) => { if (closeOnOverlay && e.target === e.currentTarget) onClose(); }}
      style={{ justifyContent: variant === 'drawer' ? (side === 'right' ? 'flex-end' : 'flex-start') : 'center', alignItems: variant === 'center' ? 'center' : 'stretch' }}
    >
      {variant === 'drawer' ? (
        <DrawerWrap
          ref={containerRef as any}
          $side={side}
          $width={width}
          $fullHeight={isFullHeight}
          role="dialog"
          aria-modal="true"
          {...labelledBy}
          {...label}
        >
          {(title || showClose) && (
            <Header>
              <div>{title}</div>
              {showClose && <CloseButton aria-label="Close" onClick={onClose}>×</CloseButton>}
            </Header>
          )}
          <Body $scroll>{children}</Body>
          {footer && <Footer>{footer}</Footer>}
        </DrawerWrap>
      ) : (
        <CenterCard ref={containerRef as any} $width={width} $fullHeight={isFullHeight} role="dialog" aria-modal="true" {...labelledBy} {...label}>
          {(title || showClose) && (
            <Header>
              <div>{title}</div>
              {showClose && <CloseButton aria-label="Close" onClick={onClose}>×</CloseButton>}
            </Header>
          )}
          <Body $scroll>{children}</Body>
          {footer && <Footer>{footer}</Footer>}
        </CenterCard>
      )}
    </Backdrop>
  );
};

export default UnifiedModal;
